<template>
  <div v-if="project" class="single-page">
    <div class="single-page__container">
      <h2>{{ formattedRank }}</h2>

      <div class="single-page__container__rank-explanation">
        <span> {{ explaination }} </span>
      </div>

      <div v-if="getPageCategory === 'projectsSingle'">
        <h3>{{ titles.propertyTitle }}</h3>
        <p>{{ project.fastighetsbeteckning }}</p>
      </div>

      <div v-if="getPageCategory !== 'buildersSingle'">
        <h3>{{ titles.communeTitle }}</h3>
        <router-link v-if="getPageCategory !== 'communesSingle'" :to="`/communes/${project.kommun}`">
          <p>{{ formattedCommune }}</p>
        </router-link>
        <p v-else>{{ formattedCommune }}</p>
      </div>

      <div v-if="getPageCategory !== 'communesSingle'">
        <h3>{{ titles.builderTitle }}</h3>
        <p v-if="getPageCategory === 'buildersSingle'">{{ project.byggherre }}</p>
        <router-link v-if="getPageCategory === 'projectsSingle'" :to="`/builders/${project.sammanlankandeByggherre}`">
          <p>{{ project.sammanlankandeByggherre }}</p>
        </router-link>
      </div>

      <div v-if="getPageCategory === 'projectsSingle' && project.husTyp">
        <h3>{{ titles.buildingTypeTitle }}</h3>
        <p>{{ project.husTyp }}</p>
      </div>

      <div>
        <h3>{{ titles.gfaTitle }}</h3>
        <p>{{ round(project.bruttoarea) }} m²</p>
      </div>

      <div>
        <h3>{{ titles.totalEmissionsTitle }}</h3>
        <p v-if="getPageCategory === projectsSingle">{{ round(project.totalaUtslapp) }} kg CO₂e</p>
        <p v-else>{{ round(project.totalaUtslapp / 1000) }} ton CO₂e</p>
      </div>

      <div>
        <h3>{{ titles.emissionsPerSquareMeterTitle }}</h3>
        <p>{{ round(project.utslappKvm) }} kg CO₂e</p>
      </div>

      <div v-if="project.kommentar" class="single-page__container__comment">
        <h3>{{ titles.commentTitle }}</h3>
        <p v-html="formattedComment"></p>
      </div>
      <div class="share-atom">
        <ShareAtom :lightTheme="true" />
      </div>
    </div>
    <!-- <GoogleMap v-if="project.koordinater" :center="mapCenter" :zoom="mapZoom" :styles="mapStyles" class="google-map">
      <Marker :options="{ position: mapCenter, title: project.fastighetsbeteckning }"></Marker>
    </GoogleMap> -->
  </div>
</template>

<script>
import { inject, computed } from 'vue';
import proj4 from 'proj4';
import api from '@/api/projects.js';
// import { GoogleMap, Marker } from 'vue3-google-map';
import ShareAtom from '../atoms/ShareAtom.vue';

export default {
  name: 'SinglePage',
  data() {
    return {
      project: {},
      sweref99: '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
      wgs84: '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs',
      long: null,
      lat: null,
      mapZoom: 15,
      mapStyles: [
        {
          featureType: 'poi.business',
          elementType: 'labels',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
      ],
    };
  },
  setup() {
    const language = inject('language');
    const isEnglish = computed(() => language.value === 'en');

    return {
      isEnglish,
    };
  },
  components: {
    // GoogleMap,
    // Marker,
    ShareAtom,
  },
  computed: {
    titles() {
      return this.isEnglish
        ? {
            propertyTitle: 'Property Designation',
            communeTitle: 'Municipality',
            builderTitle: 'Developer',
            buildingTypeTitle: 'Building Type',
            gfaTitle: 'Gross Floor Area (GFA)',
            totalEmissionsTitle: 'Total CO₂e Emissions',
            emissionsPerSquareMeterTitle: 'CO₂e Emissions Per m² (GFA)',
            commentTitle: 'Comment',
          }
        : {
            propertyTitle: 'Fastighetsbeteckning',
            communeTitle: 'Kommun',
            builderTitle: 'Byggherre',
            buildingTypeTitle: 'Byggnadstyp',
            gfaTitle: 'Bruttoarea (BTA)',
            totalEmissionsTitle: 'Totalt CO₂e-utsläpp',
            emissionsPerSquareMeterTitle: 'CO₂e-utsläpp per m² (BTA)',
            commentTitle: 'Kommentar',
          };
    },
    explanation() {
      const baseText = this.isEnglish ? 'with the highest declared CO₂e emissions' : 'med störst deklarerat CO₂e-utsläpp';

      const suffix = this.isEnglish ? (this.getPageCategory === 'projectsSingle' ? ' from new construction since 2022' : '') : ' från nybyggnation sedan 2022';

      return `${this.titles.builderTitle} ${baseText} ${suffix}`;
    },
    totalEmissions() {
      const totalUtslapp = this.project.totalaUtslapp;
      return this.isEnglish ? `${this.round(totalUtslapp / 1000)} tonnes CO₂e` : `${this.round(totalUtslapp)} kg CO₂e`;
    },
    formattedCommune() {
      if (!this.project.kommun || this.project.kommun.length === 0) return '';

      const firstLetter = this.project.kommun[0].toUpperCase();
      const remainingLetters = this.project.kommun.slice(1).toLowerCase();
      return firstLetter + remainingLetters;
    },
    mapCenter() {
      return {
        lat: this.lat,
        lng: this.long,
      };
    },
    getPageCategory() {
      return this.$route.name;
    },
    category() {
      if (this.getPageCategory === 'projectsSingle') {
        return this.isEnglish ? 'Construction project' : 'Byggprojekt';
      } else if (this.getPageCategory === 'buildersSingle') {
        return this.isEnglish ? 'Developer' : 'Byggherre';
      } else {
        return this.isEnglish ? 'Municipality' : 'Kommun';
      }
    },

    formattedRank() {
      const rankQuery = this.$route.query.rankQuery ? this.$route.query.rankQuery : 'total';
      let rank;

      if (rankQuery !== 'kvm') {
        rank = this.project.rankTotal;
      } else {
        rank = this.project.rankKvm;
      }

      if (rank && rank.plats && rank.total) {
        return `#${rank.plats}/${rank.total}`;
      }
      return '#N/A';
    },
    round() {
      return (number) => {
        return Math.round(number);
      };
    },
    explaination() {
      const isPerSquareMeter = this.$route.query.rankQuery === 'kvm';
      const baseText = this.isEnglish ? 'with the highest declared CO₂e emissions' : 'med störst deklarerat CO₂e-utsläpp';

      const suffix = this.isEnglish ? `from new construction since 2022` : `från nybyggnation sedan 2022`;

      const perSquareMeterText = this.isEnglish ? 'per m² GFA' : 'per m² BTA';

      return `${this.category} ${baseText} ${isPerSquareMeter ? perSquareMeterText : ''} ${suffix}`;
    },
    formattedComment() {
      return this.project.kommentar.replace(/\n/g, '<br>');
    },
  },
  methods: {
    convertCoordinates(x, y) {
      const [longitude, latitude] = proj4(this.sweref99, this.wgs84, [x, y]);
      return { latitude, longitude };
    },
    async fetchData() {
      const id = this.$route.params.id;
      if (this.getPageCategory === 'projectsSingle') {
        this.project = await api.getProjectById(id);
        if (this.project.koordinater) {
          let coordinates = this.project.koordinater.replace(' ', '').split(',');
          let long = parseFloat(coordinates[0]);
          let lat = parseFloat(coordinates[1]);
          let convertedCoordinates = this.convertCoordinates(lat, long);
          this.long = convertedCoordinates.longitude;
          this.lat = convertedCoordinates.latitude;
        }
      } else if (this.getPageCategory === 'buildersSingle') {
        this.project = await api.getBuilderByName(id);
      } else {
        this.project = await api.getCommuneByName(id);
      }
      if (this.project.status == 404) {
        this.$router.push({ name: 'notFound' });
      }
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.fetchData();
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id && to.name.toLowerCase().includes('single')) {
        window.scrollTo(0, 0);
        this.fetchData();
      }
    },
  },
};
</script>

<style scoped>
* {
  color: black;
}
.single-page {
  width: 100%;
}
.single-page__container {
  padding: 40px 25px;
  margin-top: 30px;
}
h2 {
  font-size: 50px;
  font-family: 'fet', sans-serif;
  margin-bottom: 0;
}
h3 {
  font-family: 'fet', sans-serif;
  margin: 0;
}
h3,
p {
  font-size: 16px;
}
p {
  font-family: 'bred', sans-serif;
}
.single-page__container__rank-explanation {
  margin-top: 10px;
  font-size: 12px;
  margin: 2px 0px 50px 0;
}
.single-page__container__rank-explanation span {
  font-family: 'fet', sans-serif;
  line-height: 1.2rem;
  margin: 0;
  font-size: 16px;
  display: block;
  font-family: 'fet';
  max-width: 25ch;
}
.single-page__container__comment {
  max-width: 1000px;
}
.share-atom {
  margin-bottom: 30px;
}
.google-map {
  width: 100%;
  height: 100vw;
  max-height: 500px;
}

@media all and (min-width: 1050px) {
  h2 {
    font-size: 75px;
  }
  .single-page__container {
    margin-top: 50px;
    padding: 40px;
  }
  .single-page__container__rank-explanation span {
    font-size: 18px;
  }
  .google-map {
    max-height: 100vh;
  }
}
</style>
